"use client";

import classNames from "classnames";
import Image from "next/image";
import { useState, useEffect } from "react";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface RoadmapItem {
  year: string;
  goal: string;
  revenue: string;
  details: string[];
  color: string;
}

interface RoadmapHighlight {
  icon: string;
  title: string;
  desc: string;
}

const RoadmapSection = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const roadmap = t("ROADMAP_SECTION_ITEMS", {
    returnObjects: true,
  }) as RoadmapItem[];

  const highlights = t("ROADMAP_SECTION_HIGHLIGHTS", {
    returnObjects: true,
  }) as RoadmapHighlight[];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    const calculateMaxHeight = () => {
      const cards = document.querySelectorAll(".roadmap-card");
      let max = 0;
      cards.forEach((card) => {
        const height = card.scrollHeight;
        if (height > max) max = height;
      });
      setMaxHeight(max);
    };

    const timerId = setTimeout(calculateMaxHeight, 100);

    window.addEventListener("resize", calculateMaxHeight);
    return () => {
      clearTimeout(timerId);
      window.removeEventListener("resize", calculateMaxHeight);
    };
  }, [isMobile]);

  return (
    <SectionContainer
      id="roadmap-section"
      className="section-shell bg-white text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack">
        <div className="section-head">
          <h2 className="section-title">
            {t("ROADMAP_SECTION_TITLE")} {" "}
            <span className="metric-label text-gray-500">{t("ROADMAP_SECTION_PERIOD")}</span>
          </h2>
          <p className="caption text-gray-600 section-lead">
            {t("ROADMAP_SECTION_SUB_TITLE_1")} {" "}
            <strong className="text-[#207AE8]">{t("ROADMAP_SECTION_SUB_TITLE_POINT_1")}</strong> {" "}
            {t("ROADMAP_SECTION_SUB_TITLE_2")} {" "}
            <strong className="text-[#5BBF4D]">{t("ROADMAP_SECTION_SUB_TITLE_POINT_2")}</strong>
            {t("ROADMAP_SECTION_SUB_TITLE_3")}
          </p>
        </div>

        <div className="relative flex flex-col md:flex-row md:flex-nowrap md:justify-start items-center md:items-start gap-8 md:gap-0 md:overflow-x-auto md:py-6">
          {roadmap.map((item, i) => (
            <div
              key={item.year}
              className={classNames(
                "roadmap-card relative w-full md:w-[20%] bg-white shadow-md rounded-xl p-5 border border-gray-100 hover:shadow-lg transition-all flex-shrink-0 flex flex-col gap-4 py-12!"
              )}
              style={
                !isMobile
                  ? {
                      marginTop: `${(roadmap.length - 1 - i) * 4}rem`,
                      height: maxHeight ? `${maxHeight}px` : "auto",
                    }
                  : {}
              }
            >
              <div
                className={`absolute top-0 ${
                  isMobile ? "left-0 rounded-r-[8px]" : "right-0 rounded-l-[8px]"
                } w-[130px] h-10 ${item.color} text-white font-semibold flex items-center justify-center shadow-md`}
              >
                {item.year}
              </div>

              <div>
                <h4 className="metric-label text-gray-900! font-semibold!">
                  {item.goal}
                </h4>

                <p className="label w-fit font-semibold my-2!">
                  {t("ROADMAP_SECTION_TARGET_REVENUE")} {item.revenue}
                </p>
              </div>

              <ul className="mt-3 text-[clamp(0.9rem,1.3vw,1rem)] space-y-4! whitespace-pre-wrap pb-5">
                {item.details.map((detail) => (
                  <li key={detail}>&#8594; {detail}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {highlights.map((h) => (
            <div
              key={h.title}
              className="flex flex-col sm:flex-row items-center gap-4 md:gap-8 bg-gradient-to-b from-white to-gray-50 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="relative w-14 h-14 flex-shrink-0">
                <Image
                  src={h.icon}
                  alt={h.title}
                  fill
                  sizes="56px"
                  className="object-contain"
                />
              </div>
              <div>
                <h4 className="text font-semibold text-[#207AE8] mb-1">
                  {h.title}
                </h4>
                <p className="caption text-gray-700 leading-relaxed max-w-[300px]">
                  {h.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default RoadmapSection;
